import showdown from 'showdown';
import TurndownService from 'turndown';

/**
 * Convert Markdown to HTML with Quill-compatible formatting
 * Handles nested lists, indentation, and other special formatting
 *
 * @param markdown - The markdown string to convert
 * @returns HTML string compatible with ReactQuill
 */
export function markdownToHtml(markdown: string): string {
  // Normalize list indentation: convert 2-space indents to 4-space (Showdown standard)
  const lines = markdown.split('\n');
  const normalizedLines = lines.map((line) => {
    // Count leading spaces before list marker
    const match = line.match(/^(\s*)(-|\d+\.)\s/);
    if (match) {
      const indent = match[1];
      const spaces = indent.length;
      // Convert to 4-space indents (Showdown standard)
      const level = Math.floor(spaces / 2);
      const newIndent = '    '.repeat(level); // 4 spaces per level
      return newIndent + line.substring(indent.length);
    }
    return line;
  });
  const normalizedMarkdown = normalizedLines.join('\n');

  // Convert markdown to HTML using showdown
  const converter = new showdown.Converter({
    simpleLineBreaks: false,
    requireSpaceBeforeHeadingText: true,
  });
  let html = converter.makeHtml(normalizedMarkdown);

  // Convert nested lists to Quill's indent format
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Recursively flatten nested lists and add indent classes
  const flattenList = (parentList: Element, indentLevel: number = 0): Element[] => {
    const items = Array.from(parentList.children);
    const flatItems: Element[] = [];

    items.forEach((item) => {
      if (item.tagName === 'LI') {
        // Add indent class if needed
        if (indentLevel > 0) {
          item.classList.add(`ql-indent-${indentLevel}`);
        }

        // Find nested lists within this LI
        const nestedLists = Array.from(item.children).filter(
          (c) => c.tagName === 'UL' || c.tagName === 'OL'
        );

        // Add current item
        flatItems.push(item);

        // Process nested lists
        nestedLists.forEach((nestedList) => {
          const nestedItems = flattenList(nestedList, indentLevel + 1);
          flatItems.push(...nestedItems);
          nestedList.remove(); // Remove the nested list wrapper
        });
      }
    });

    return flatItems;
  };

  // Process each top-level list
  const allLists = Array.from(doc.querySelectorAll('ul, ol'));
  const topLevelLists = allLists.filter((list) => {
    // Check if this list has a parent that is also a list
    let parent = list.parentElement;
    while (parent && parent !== doc.body) {
      if (parent.tagName === 'UL' || parent.tagName === 'OL' || parent.tagName === 'LI') {
        return false; // This is a nested list, skip it
      }
      parent = parent.parentElement;
    }
    return true; // This is a top-level list
  });

  topLevelLists.forEach((list) => {
    const flatItems = flattenList(list, 0);

    // Clear the list and add flattened items
    list.innerHTML = '';
    flatItems.forEach((item) => list.appendChild(item));
  });

  // Convert HTML paragraphs with inline styles back to Quill indent classes
  doc.querySelectorAll('p[style*="margin-left"]').forEach((elem) => {
    const p = elem as HTMLElement;
    const style = p.getAttribute('style');
    if (style) {
      // Extract margin-left value (e.g., "margin-left: 4em" -> 4)
      const match = style.match(/margin-left:\s*(\d+)em/);
      if (match) {
        const marginEm = parseInt(match[1]);
        const indentLevel = Math.round(marginEm / 2); // 2em per indent level
        if (indentLevel > 0) {
          p.classList.add(`ql-indent-${indentLevel}`);
          // Remove the inline style since Quill will handle it
          p.removeAttribute('style');
        }
      }
    }
  });

  return doc.body.innerHTML;
}

/**
 * Convert HTML to Markdown with proper formatting
 * Handles Quill-specific classes and nested lists
 *
 * @param html - The HTML string to convert
 * @returns Markdown string
 */
export function htmlToMarkdown(html: string): string {
  // Convert Quill's indent classes back to nested lists
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Handle indented paragraphs (convert to HTML with inline styles)
  doc.querySelectorAll('p[class*="ql-indent-"]').forEach((elem) => {
    const p = elem as HTMLElement;
    let indentLevel = 0;
    for (let i = 1; i <= 8; i++) {
      if (p.classList.contains(`ql-indent-${i}`)) {
        indentLevel = i;
        p.classList.remove(`ql-indent-${i}`);
        break;
      }
    }

    if (indentLevel > 0) {
      // Add inline style for visual indentation (2em per level)
      const indentEm = indentLevel * 2;
      p.style.marginLeft = `${indentEm}em`;
    }
  });

  // Reconstruct nested list structure from flat list with indent classes
  doc.querySelectorAll('ul, ol').forEach((list) => {
    const listTag = list.tagName;
    const items = Array.from(list.querySelectorAll(':scope > li'));

    // Build a stack to track nesting
    const root = list;
    root.innerHTML = ''; // Clear and rebuild

    let currentContainers: Element[] = [root]; // Stack of containers (list elements)
    let currentLevel = 0;

    items.forEach((item) => {
      // Get indent level
      let indentLevel = 0;
      for (let i = 1; i <= 8; i++) {
        if (item.classList.contains(`ql-indent-${i}`)) {
          indentLevel = i;
          item.classList.remove(`ql-indent-${i}`);
          break;
        }
      }

      // Adjust stack based on indent level
      if (indentLevel > currentLevel) {
        // Need to go deeper - create nested lists
        for (let i = currentLevel; i < indentLevel; i++) {
          const newList = doc.createElement(listTag);
          const lastItem = currentContainers[currentContainers.length - 1].lastElementChild;
          if (lastItem && lastItem.tagName === 'LI') {
            lastItem.appendChild(newList);
          } else {
            // Shouldn't happen, but handle gracefully
            currentContainers[currentContainers.length - 1].appendChild(newList);
          }
          currentContainers.push(newList);
        }
      } else if (indentLevel < currentLevel) {
        // Need to go up - pop containers
        const diff = currentLevel - indentLevel;
        for (let i = 0; i < diff; i++) {
          currentContainers.pop();
        }
      }

      // Add item to current container
      currentContainers[currentContainers.length - 1].appendChild(item);
      currentLevel = indentLevel;
    });
  });

  const turndownService = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    preformattedCode: false,
  });

  // Override list item rule to match LLM-generated format:
  // - Single space after bullet (not 3 spaces)
  // - 2-space indentation for nested items (not 4 spaces)
  turndownService.addRule('listItem', {
    filter: 'li',
    replacement: function (content, node, options) {
      content = content
        .replace(/^\n+/, '') // remove leading newlines
        .replace(/\n+$/, '\n') // replace trailing newlines with just a single one
        .replace(/\n/gm, '\n  '); // indent nested content with 2 spaces (not 4)

      let prefix = options.bulletListMarker + ' ';

      const parent = node.parentNode as Element;
      if (parent && parent.nodeName === 'OL') {
        const start = parent.getAttribute('start');
        const index = Array.prototype.indexOf.call(parent.children, node);
        prefix = (start ? Number(start) + index : index + 1) + '. ';
      }

      return prefix + content + (node.nextSibling && !/\n$/.test(content) ? '\n' : '');
    },
  });

  // Preserve indented paragraphs as HTML
  turndownService.addRule('indentedParagraph', {
    filter: function (node: any) {
      return node.nodeName === 'P' && node.style.marginLeft;
    },
    replacement: function (content: string, node: any) {
      const marginLeft = node.style.marginLeft;
      return `<p style="margin-left: ${marginLeft}">${content}</p>\n\n`;
    },
  });

  // Preserve underline tags as inline HTML (markdown doesn't have native underline)
  turndownService.addRule('preserveUnderline', {
    filter: function (node: any) {
      return node.nodeName === 'U';
    },
    replacement: function (content: string) {
      return `<u>${content}</u>`;
    },
  });

  return turndownService.turndown(doc.body.innerHTML);
}
